/**
 * Held Zuora welcomes — reviewed manual release (owner decision 2026-10-03).
 *
 * Lists welcomes the system HELD because the Zuora re-fetch could not confirm a new signup. An admin checks the
 * account in Zuora, then either releases the welcome (stating what Zuora shows) or dismisses it. Who, when and why
 * are recorded. The server refuses any release a suppression forbids; this page only shows the reason.
 *
 * Route: /admin/zuora-held-welcomes (super_admin / admin only; the RPCs enforce the same server-side).
 */
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import Button from "../../components/common/Button";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import Modal from "../../components/common/Modal";
import {
  dismissHeldWelcome,
  getHeldWelcomes,
  releaseHeldWelcome,
  type HeldWelcomeResult,
  type HeldWelcomeRow,
} from "../../services/zuoraHeldWelcomes.service";

const HOLD_REASON_TEXT: Record<string, string> = {
  account_not_found_in_zuora: "Account not found in Zuora",
  no_subscriptions_in_zuora: "Zuora shows no subscriptions on the account",
  timed_out_unconfirmed: "Re-fetch did not finish in time",
  superseded_by_new_joining: "Superseded by a newer joining",
};

const REFUSAL_TEXT: Record<string, string> = {
  switched_off:
    "The contact fetch switch is off, so no welcome can be released.",
  contact_not_active:
    "This person is no longer an active member (or the contact was merged).",
  superseded_by_newer_joining:
    "This person has joined again since; this membership is no longer current.",
  blocked_by_predicate:
    "Another block applies (a membership restoration or a hold on the account).",
  not_held_or_already_reviewed:
    "This welcome is no longer held, or someone already reviewed it.",
  repair_origin:
    "This membership is a restoration of an earlier one, which never gets a welcome.",
  account_hold:
    "There is a hold on this Zuora account (for example an identity review).",
  no_active_journey:
    "No active welcome journey exists for this kind of member, so nothing would be sent.",
  classification_conflict:
    "The other journey (new vs returning member) already started for this membership.",
  no_joining_row: "The joining record for this membership is missing.",
};

function preflightText(p: string | null): string {
  if (p === null) {
    return "Ready";
  }
  return refusalText({ ok: false, outcome: "refused", reason: p });
}

function holdReasonText(r: string | null): string {
  if (!r) {
    return "—";
  }
  if (HOLD_REASON_TEXT[r]) {
    return HOLD_REASON_TEXT[r];
  }
  if (r.startsWith("unconfirmed_")) {
    return "Re-fetch incomplete or failed after all attempts";
  }
  return r;
}

function refusalText(res: HeldWelcomeResult): string {
  const r = res.reason ?? res.outcome;
  if (REFUSAL_TEXT[r]) {
    return REFUSAL_TEXT[r];
  }
  if (r.startsWith("email:")) {
    return "This person's email is suppressed or opted out, so no welcome can be sent.";
  }
  if (r.startsWith("excluded_")) {
    return "This membership came from an import, Pardot backfill or restoration, which never gets a welcome.";
  }
  return r;
}

function fmtTs(ts: string | null): string {
  if (!ts) {
    return "—";
  }
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? "—" : format(d, "MMM d, yyyy h:mm a");
}

type Action = { kind: "release" | "dismiss"; row: HeldWelcomeRow } | null;

const ZuoraHeldWelcomes: React.FC = () => {
  const qc = useQueryClient();
  const [includeReviewed, setIncludeReviewed] = useState(false);
  const [action, setAction] = useState<Action>(null);
  const [classification, setClassification] = useState<"new" | "reinstatement">(
    "new",
  );
  const [reason, setReason] = useState("");
  const [checked, setChecked] = useState(false);
  const [message, setMessage] = useState<{
    tone: "ok" | "error";
    text: string;
  } | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["zuora-held-welcomes", includeReviewed],
    queryFn: () => getHeldWelcomes(includeReviewed),
  });

  const close = () => {
    setAction(null);
    setReason("");
    setChecked(false);
    setClassification("new");
  };

  const mut = useMutation({
    mutationFn: async () => {
      if (!action) {
        throw new Error("No action");
      }
      return action.kind === "release"
        ? releaseHeldWelcome(
            action.row.deferred_id,
            classification,
            reason.trim(),
          )
        : dismissHeldWelcome(action.row.deferred_id, reason.trim());
    },
    onSuccess: (res) => {
      if (res.ok) {
        const n =
          (res.release?.enrolled ?? 0) +
          (res.release?.enrolled_before ?? 0) +
          (res.release?.already_enrolled ?? 0);
        // R6-1: journeys skipped by their own "no re-enrollment" setting are NOT scheduled; say so separately
        const skipped = res.release?.not_reenrollable ?? 0;
        setMessage({
          tone: "ok",
          text:
            res.outcome === "released"
              ? `Welcome released: ${n} journey${n === 1 ? "" : "s"} scheduled.` +
                (skipped > 0
                  ? ` ${skipped} journey${skipped === 1 ? " was" : "s were"} not resent because ${skipped === 1 ? "it does" : "they do"} not allow re-enrollment.`
                  : "")
              : "Held welcome dismissed.",
        });
      } else if (res.outcome === "release_incomplete") {
        const failed = res.release?.failed ?? [];
        setMessage({
          tone: "error",
          text:
            `Not released yet: ${failed.length} journey${failed.length === 1 ? "" : "s"} could not be scheduled ` +
            `(${failed.map((f) => `${f.name}: ${f.error}`).join("; ")}). ` +
            "The welcome is still held. Fix the journey, then press Retry release; journeys already scheduled are not sent twice.",
        });
      } else {
        setMessage({
          tone: "error",
          text: `Not released: ${refusalText(res)}`,
        });
      }
      close();
      void qc.invalidateQueries({ queryKey: ["zuora-held-welcomes"] });
    },
    onError: (e: Error) => setMessage({ tone: "error", text: e.message }),
  });

  const rows = q.data ?? [];
  const reasonOk = reason.trim().length >= 10;
  const canSubmit =
    reasonOk && (action?.kind === "dismiss" || checked) && !mut.isPending;

  return (
    <div className="p-6 space-y-4" data-testid="zuora-held-welcomes-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Held Zuora welcomes
          </h1>
          <p className="mt-1 text-sm text-gray-600 max-w-3xl">
            These new-member welcomes were held because Zuora could not confirm
            a new signup. Check each account in Zuora. Release a welcome only if
            Zuora shows a genuine new signup or a returning member; otherwise
            dismiss it. The system still refuses a release if the person is no
            longer active, their email is suppressed, or the membership came
            from an import or restoration.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
          <input
            type="checkbox"
            checked={includeReviewed}
            onChange={(e) => setIncludeReviewed(e.target.checked)}
          />
          Show reviewed
        </label>
      </div>

      {message && (
        <div
          role="status"
          className={`rounded-md px-4 py-2 text-sm ${message.tone === "ok" ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}
        >
          {message.text}
        </div>
      )}

      {q.isLoading ? (
        <LoadingSpinner />
      ) : q.isError ? (
        <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">
          {(q.error as Error).message}
        </div>
      ) : rows.length === 0 ? (
        <div
          className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-600"
          data-testid="held-welcomes-empty"
        >
          No held welcomes{includeReviewed ? " or reviewed decisions" : ""}.
          This is the expected state while the welcome rule is off.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table
            className="min-w-full divide-y divide-gray-200 text-sm"
            data-testid="held-welcomes-table"
          >
            <thead className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2">Contact</th>
                <th className="px-4 py-2">Zuora account</th>
                <th className="px-4 py-2">Joined</th>
                <th className="px-4 py-2">Why held</th>
                <th className="px-4 py-2">Server check</th>
                <th className="px-4 py-2">Decision</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => {
                const retry =
                  r.last_attempt !== null && r.last_attempt.completed === false;
                return (
                  <React.Fragment key={r.deferred_id}>
                    <tr className="align-top">
                      <td className="px-4 py-2">
                        <a
                          className="text-blue-600 hover:underline"
                          href={`/contacts/${r.contact_id}`}
                        >
                          {r.contact_name ?? "Unnamed contact"}
                        </a>
                        <div className="text-xs text-gray-500">
                          {r.contact_email ?? "no email"}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div>{r.account_number ?? "—"}</div>
                        <div className="text-xs text-gray-500 font-mono">
                          {r.zuora_account_id}
                        </div>
                      </td>
                      <td className="px-4 py-2">{fmtTs(r.joined_at)}</td>
                      <td className="px-4 py-2">
                        <div>{holdReasonText(r.hold_reason)}</div>
                        <button
                          type="button"
                          className="text-xs text-blue-600 hover:underline"
                          onClick={() =>
                            setExpanded(
                              expanded === r.deferred_id ? null : r.deferred_id,
                            )
                          }
                        >
                          {expanded === r.deferred_id
                            ? "Hide evidence"
                            : "Show evidence"}
                        </button>
                      </td>
                      <td className="px-4 py-2">
                        {r.review_action ? (
                          <span className="text-gray-500">—</span>
                        ) : r.preflight === null ? (
                          <span className="text-green-700">Ready</span>
                        ) : (
                          <span
                            className="text-red-700"
                            data-testid="held-welcome-preflight"
                          >
                            {preflightText(r.preflight)}
                          </span>
                        )}
                        {retry && r.last_attempt && (
                          <div
                            className="mt-1 text-xs text-red-700"
                            data-testid="held-welcome-last-attempt"
                          >
                            Last release attempt{" "}
                            {fmtTs(r.last_attempt.attempted_at)} by{" "}
                            {r.last_attempt.by ?? "unknown"} did not finish:{" "}
                            {(r.last_attempt.failed ?? [])
                              .map((f) => `${f.name}: ${f.error}`)
                              .join("; ")}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {r.review_action ? (
                          <div className="text-xs text-gray-700">
                            {r.review_action === "released"
                              ? `Released (${r.review_classification})`
                              : "Dismissed"}{" "}
                            by {r.reviewed_by_name ?? "unknown"},{" "}
                            {fmtTs(r.reviewed_at)}
                            <div className="text-gray-500">
                              {r.review_reason}
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() =>
                                setAction({ kind: "release", row: r })
                              }
                            >
                              {retry ? "Retry release…" : "Release…"}
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() =>
                                setAction({ kind: "dismiss", row: r })
                              }
                            >
                              Dismiss…
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {expanded === r.deferred_id && (
                      <tr>
                        <td colSpan={6} className="bg-gray-50 px-4 py-2">
                          <div className="text-xs text-gray-600">
                            Attempts: {r.attempts} · Listed subscriptions:{" "}
                            {(r.listed_ids ?? []).join(", ") || "none"}
                          </div>
                          <pre
                            className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap text-xs text-gray-700"
                            data-testid="held-welcome-evidence"
                          >
                            {JSON.stringify(
                              { fetch: r.last_fetch, evidence: r.evidence },
                              null,
                              2,
                            )}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={action !== null}
        onClose={close}
        title={
          action?.kind === "release"
            ? "Release this welcome"
            : "Dismiss this held welcome"
        }
      >
        {action && (
          <div className="space-y-4 text-sm" data-testid="held-welcome-dialog">
            <p className="text-gray-700">
              {action.row.contact_name ?? "Unnamed contact"} · Zuora account{" "}
              {action.row.account_number ?? action.row.zuora_account_id}
            </p>
            {action.kind === "release" && (
              <>
                <fieldset className="space-y-1">
                  <legend className="font-medium text-gray-900">
                    What does Zuora show?
                  </legend>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="cls"
                      checked={classification === "new"}
                      onChange={() => setClassification("new")}
                    />
                    A new signup (first subscription term). Sends the new-member
                    welcome.
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="cls"
                      checked={classification === "reinstatement"}
                      onChange={() => setClassification("reinstatement")}
                    />
                    A returning member (an earlier membership had ended). Sends
                    the returning-member journey.
                  </label>
                  <p className="text-xs text-gray-500">
                    If Zuora shows a renewal, or you are not sure, dismiss
                    instead.
                  </p>
                </fieldset>
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => setChecked(e.target.checked)}
                  />
                  I checked this account in Zuora.
                </label>
              </>
            )}
            <label className="block">
              <span className="font-medium text-gray-900">
                Reason (recorded with your name)
              </span>
              <textarea
                className="mt-1 w-full rounded-md border border-gray-300 p-2"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={
                  action.kind === "release"
                    ? "e.g. Zuora shows one first-term subscription started today"
                    : "e.g. Zuora shows a renewal since 2019"
                }
              />
              {!reasonOk && (
                <span className="text-xs text-gray-500">
                  At least 10 characters.
                </span>
              )}
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={close}>
                Cancel
              </Button>
              <Button
                variant={action.kind === "release" ? "primary" : "danger"}
                disabled={!canSubmit}
                isLoading={mut.isPending}
                onClick={() => mut.mutate()}
              >
                {action.kind === "release" ? "Release welcome" : "Dismiss"}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ZuoraHeldWelcomes;
